import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { collection, getDocs, writeBatch } from 'firebase/firestore';

const defaultServiceCategories = [
  {
    value: 'permanentMakeup',
    label: 'Permanent Makeup',
    icon: '👁️‍🗨️',
    color: 'primary',
    translationKey: 'permanentMakeup',
    isActive: true,
    order: 1
  },
  {
    value: 'hairRemoval',
    label: 'Hair Removal',
    icon: '🪒',
    color: 'info',
    translationKey: 'hairRemoval',
    isActive: true,
    order: 2
  },
  {
    value: 'aesthetics',
    label: 'Aesthetics',
    icon: '🌿',
    color: 'warning',
    translationKey: 'aesthetics',
    isActive: true,
    order: 3
  },
  {
    value: 'consultation',
    label: 'Consultation',
    icon: '🗨️',
    color: 'danger',
    translationKey: 'consultation',
    isActive: true,
    order: 4
  },
  {
    value: 'privateLesson',
    label: 'Private Lesson',
    icon: '🎓',
    color: 'default',
    translationKey: 'privateLesson',
    isActive: true,
    order: 5
  }
];

export const businessConfigService = {
  /**
   * Migrate service_categories to all existing business_configs
   * One-time migration function - use with caution
   * @returns {Promise<Object>} Migration result summary
   */
  async migrateServiceCategories() {
    try {
      console.log('🚀 Starting service_categories migration...');
      
      const businessConfigsRef = collection(db, 'business_configs');
      const snapshot = await getDocs(businessConfigsRef);
      
      if (snapshot.empty) {
        console.log('📭 No business_configs found to migrate');
        return { updated: 0, skipped: 0, total: 0 };
      }
      
      const batch = writeBatch(db);
      let updatedCount = 0;
      let skippedCount = 0;
      
      snapshot.forEach((userDoc) => {
        const userId = userDoc.id;
        const data = userDoc.data();
        
        // Check if service_categories already exists
        if (data.service_categories && Array.isArray(data.service_categories)) {
          console.log(`⏭️ Skipping user ${userId} - service_categories already exists`);
          skippedCount++;
          return;
        }
        
        // Add service_categories to this user's business_config
        batch.update(userDoc.ref, {
          service_categories: defaultServiceCategories,
          updatedAt: new Date()
        });
        
        updatedCount++;
        console.log(`✅ Queued user ${userId} for update`);
      });
      
      if (updatedCount > 0) {
        console.log(`🔄 Committing batch update for ${updatedCount} users...`);
        await batch.commit();
        console.log(`🎉 Successfully updated ${updatedCount} business_configs`);
      }
      
      const result = {
        updated: updatedCount,
        skipped: skippedCount,
        total: snapshot.size,
        success: true
      };
      
      console.log('📊 Migration Summary:', result);
      return result;
      
    } catch (error) {
      console.error('❌ Service categories migration failed:', error);
      throw new Error(`Migration failed: ${error.message}`);
    }
  },

  /**
   * Get business configuration for a user
   * @param {string} userId - Firebase user UID
   * @returns {Promise<Object|null>} Business config or null if not found
   */
  async getBusinessConfig(userId) {
    try {
      const configRef = doc(db, 'business_configs', userId);
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        return { id: configSnap.id, ...configSnap.data() };
      }

      return null;
    } catch (error) {
      console.error('Error fetching business config:', error);
      throw new Error('Failed to fetch business configuration');
    }
  },

  /**
   * Create new business configuration
   * @param {string} userId - Firebase user UID
   * @param {Object} config - Business configuration object
   * @returns {Promise<Object>} Created config
   */
  async createBusinessConfig(userId, config) {
    try {
      const configRef = doc(db, 'business_configs', userId);

      // Check if config already exists to prevent accidental overwrites
      const existingConfig = await getDoc(configRef);

      if (existingConfig.exists()) {
        console.warn('⚠️ Business config already exists for user:', userId);
        console.warn('Using merge to prevent data loss');
      }

      const configWithTimestamp = {
        ...config,
        createdAt: new Date(),
        updatedAt: new Date()
      };

      // CRITICAL: Use merge option to prevent data loss
      await setDoc(configRef, configWithTimestamp, { merge: true });

      console.log('✅ Business config created/updated with merge:', userId);
      return { id: userId, ...configWithTimestamp };
    } catch (error) {
      console.error('Error creating business config:', error);
      throw new Error('Failed to create business configuration');
    }
  },

  /**
   * Update existing business configuration
   * @param {string} userId - Firebase user UID
   * @param {Object} updates - Partial config updates
   * @returns {Promise<void>}
   */
  async updateBusinessConfig(userId, updates) {
    try {
      const configRef = doc(db, 'business_configs', userId);
      const updateWithTimestamp = {
        ...updates,
        updatedAt: new Date()
      };

      await updateDoc(configRef, updateWithTimestamp);
    } catch (error) {
      console.error('Error updating business config:', error);
      throw new Error('Failed to update business configuration');
    }
  },
};